// components/generic/PremiumFeatureGate/PremiumFeatureGate.tsx
import React from "react";
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Group,
  ThemeIcon,
  Box,
  Divider,
} from "@mantine/core";
import { IconLock, IconRocket } from "@tabler/icons-react";
import { useApp } from "@/contexts";
import { FEATURE_CODES } from "@/config";

export interface PremiumFeatureGateProps {
  /** Feature code to check against the user's plan */
  featureCode: keyof typeof FEATURE_CODES | string;

  /** Title shown when feature is locked */
  featureTitle?: string;

  /** Description/showcase of what the feature does (shown when locked) */
  featureDescription?: React.ReactNode;

  /** The actual page content to render when feature is available */
  children: React.ReactNode;

  /** URL to redirect for upgrade (defaults to /account/billing) */
  upgradeUrl?: string;

  /** Custom upgrade button text */
  upgradeButtonText?: string;

  /** Whether to show a compact version (inline vs full page) */
  compact?: boolean;

  /** Optional: Override the feature check (useful for testing) */
  forceEnabled?: boolean;
}

export const PremiumFeatureGate: React.FC<PremiumFeatureGateProps> = ({
  featureCode,
  featureTitle,
  featureDescription,
  children,
  upgradeUrl = "/account/subscription",
  upgradeButtonText = "Upgrade Your Plan",
  compact = false,
  forceEnabled,
}) => {
  const { hasAppPlanFeature, getAppPlanFeature, isInitializing } = useApp();

  // Check if feature is enabled
  const isEnabled =
    forceEnabled !== undefined ? forceEnabled : hasAppPlanFeature(featureCode);

  const featureDetails = getAppPlanFeature(featureCode);

  // While initializing, show nothing or a loader
  if (isInitializing) {
    return null;
  }

  // Feature is enabled - render children
  if (isEnabled) {
    return <>{children}</>;
  }

  // Feature is locked - show upgrade prompt
  const title =
    featureTitle || featureDetails?.featureName || "Premium Feature";

  if (compact) {
    return (
      <Paper p="md" radius="md" withBorder>
        <Group>
          <ThemeIcon size="lg" radius="xl" color="yellow" variant="light">
            <IconLock size={18} />
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text fw={600}>{title}</Text>
            <Text size="sm" c="dimmed">
              This feature is not available on your current plan.
            </Text>
          </Box>
          <Button
            component="a"
            href={upgradeUrl}
            variant="light"
            color="blue"
            size="sm"
            leftSection={<IconRocket size={16} />}
          >
            {upgradeButtonText}
          </Button>
        </Group>
      </Paper>
    );
  }

  return (
    <Container size="md" py="xl">
      <Paper p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          {/* Lock Icon */}
          <ThemeIcon size={64} radius="xl" color="yellow" variant="light">
            <IconLock size={32} />
          </ThemeIcon>

          {/* Title */}
          <Title order={2} ta="center">
            {title}
          </Title>

          {/* Subtitle */}
          <Text c="dimmed" ta="center" maw={500}>
            This feature is not available on your current plan. Upgrade to
            unlock this and other powerful features.
          </Text>

          {/* Upgrade Button */}
          <Button
            component="a"
            href={upgradeUrl}
            size="lg"
            leftSection={<IconRocket size={20} />}
          >
            {upgradeButtonText}
          </Button>

          {/* Feature Description */}
          {featureDescription && (
            <>
              <Divider
                w="100%"
                my="md"
                label="What you'll get"
                labelPosition="center"
              />
              <Box w="100%">{featureDescription}</Box>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
